// <purpose>The PURE half of the press (decide, then step; decision 2026-10-28): what the ticket
// looks like the moment the clerk presses, before the server answers, and how the after-press card
// words the press. Genehmigen changes the mail; Nächste Mail changes what the clerk is looking at.
// The press is optimistic — the verdict shows at once and a refusal rolls it back — so the patch
// below must be the SAME shape the server's re-read will bring, or the bar would change twice.</purpose>
// <dependencies>Core's own writes, mirrored: a letter approval stamps a `reply` claim (an `archive`
// claim when the letter is blank) via approve and moves the row to `approved`; a triage approval
// stamps that kind's claim and moves an OPEN row to `approved` (a settled row keeps its status); a
// decline moves the row to `declined` with its "instead". Nothing here calls the server.</dependencies>
// <constraints>No React. German strings are the press's own words (code-owned like Genehmigen);
// a state word is served (decision.approve), a kind word is the served one (draftKindWord) wrapped
// in fixed German, never inflected.</constraints>
import type { DeclineInstead, DraftKind, TicketDetail, TicketDecline } from '../serverTypes';
import type { FaceFn } from './categoryMeta';
import { draftKindWord } from './draftClaims';

/** The verdict a press records. */
export type PressVerdict = 'approved' | 'declined';

/** The undo window of a press (the card's Rückgängig). Nothing navigates when it runs out. */
export const PRESS_UNDO_MS = 30000;

/** The yes badge's word on a tenant that states no approved kind (one-kind tenants): the SERVED
 *  `decision.approve` face („Genehmigt" on every seed) — the same word Statistik's card reads. */
export const approvedWord = (face: FaceFn): string => face('decision', 'approve') ?? '';

/** The yes badge's word for one approved kind: „Weiterleitung genehmigt", „Ablegen genehmigt". */
export const approvedKindWord = (kind: DraftKind, face: FaceFn): string => `${draftKindWord(kind, face)} genehmigt`;

const nowIso = () => new Date().toISOString();

/** The letter kind core claims for a letter-flow approval: the reply, or the filing when the
 *  letter is blank (the same test core runs on the edited-else-composed text). */
export const letterApproveKind = (ticket: TicketDetail): DraftKind => {
  const s = ticket.suggestion;
  const letter = s?.editedText ?? s?.suggestionText ?? '';
  const hasScaffold = !!s?.scaffold?.segments?.length;
  return !hasScaffold && !letter.trim() ? 'archive' : 'reply';
};

/** The ticket as the server will read it after a letter-flow Genehmigen. */
export function applyLetterApprove(ticket: TicketDetail): TicketDetail {
  return applyKindApprove(ticket, letterApproveKind(ticket));
}

/** The ticket as the server will read it after one kind's approval. An open row moves to
 *  `approved`; a row already settled (the second WIM letter, from Entschieden) keeps its status. */
export function applyKindApprove(ticket: TicketDetail, kind: DraftKind): TicketDetail {
  const s = ticket.suggestion;
  const claims = { ...(s?.claims ?? {}) };
  const prior = claims[kind];
  claims[kind] = { ...(prior ?? {}), type: kind, via: 'approve', confirmedAt: prior?.confirmedAt ?? nowIso() };
  return {
    ...ticket,
    status: ticket.status === 'suggestion_ready' ? 'approved' : ticket.status,
    suggestion: s ? { ...s, claims } : s,
  };
}

/** The decline's stored shape for an "instead" (null = „Ohne Angabe ablegen"). */
export const declineOf = (instead: DeclineInstead | null): TicketDecline => ({
  insteadKind: instead ? instead.kind : null,
  insteadValue: instead && instead.kind === 'primary_axis' ? instead.value : null,
  declinedAt: nowIso(),
});

/** The ticket as the server will read it after „Nicht hilfreich". */
export function applyDecline(ticket: TicketDetail, instead: DeclineInstead | null): TicketDetail {
  return { ...ticket, status: 'declined', decline: declineOf(instead) };
}

/** The ticket put back open — the undo's optimistic face. Claims made by approval are taken back
 *  (core's undo reverts every approve claim of the mail); copies and the receipt stay. */
export function applyReopen(ticket: TicketDetail): TicketDetail {
  const s = ticket.suggestion;
  const claims = s?.claims
    ? Object.fromEntries(Object.entries(s.claims).filter(([, c]) => c?.via !== 'approve'))
    : s?.claims;
  return { ...ticket, status: 'suggestion_ready', decline: null, suggestion: s ? { ...s, claims } : s };
}

/** What the card's heading says the press did, before the subject. */
export function pressHeadingVerdict(
  p: { verdict: PressVerdict; lastKind: DraftKind | null; declineWord: string | null; statesKind: boolean },
  face: FaceFn,
): string {
  if (p.verdict === 'declined') return p.declineWord ? `Nicht hilfreich · ${p.declineWord}` : 'Nicht hilfreich';
  return p.statesKind && p.lastKind ? approvedKindWord(p.lastKind, face) : approvedWord(face);
}
